// react
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// api
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';

// components
import useStableSearchParams from '@/hooks/useStableSearchParams';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

// relative
import type { ReadUnitDto } from '../types';

const NO_UNITS: ReadUnitDto[] = [];

function useSelectedUnits(): [ReadUnitDto[], (ids: number[]) => void] {
  const accountId = useSelector(getSelectedAccount).AccountId;
  const [searchParams, setSearchParams] = useStableSearchParams();

  const { currentData: units, isSuccess: unitsOk } =
    useUnitsGetCollectionAsyncByqueryOptionsQuery(
      { 'Account.AccountId': `eq(${accountId})` },
      { skip: !accountId },
    );

  const selectedUnitIds = searchParams.get('units');

  // Stable identity: consumers use this array as an effect dependency.
  const selectedUnits = useMemo(() => {
    if (!unitsOk || !units?.length || !selectedUnitIds) return NO_UNITS;
    const idSet = new Set(selectedUnitIds.split(',').map(Number));
    return units.filter((u) => idSet.has(Number(u.UnitId)));
  }, [unitsOk, units, selectedUnitIds]);

  const setSelectedUnitIdsInUrl = (ids: number[]) => {
    setSearchParams((prev: URLSearchParams) => {
      const nextParams = new URLSearchParams(prev);
      if (ids && ids.length) {
        nextParams.set('units', ids.join(','));
      } else {
        nextParams.delete('units');
      }
      return nextParams;
    });
  };

  return [selectedUnits, setSelectedUnitIdsInUrl];
}

export default useSelectedUnits;
